"use client";

import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, ShoppingCart, Heart, CheckCircle, X } from "lucide-react";
import Header from "../Profile/Header";
import CustomizationModal from "./CustomizationModal";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { useCategories } from "../context/CategoriesContext";
import toast from 'react-hot-toast';

// Enhanced ProductCard component with cart functionality
function ProductCard({ product, className, onCustomizeClick }) {
  const [isHovered, setIsHovered] = useState(false);
  const { categories = [] } = useCategories();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[String(c.id)] = c.title;
    });
    return map;
  }, [categories]);

  // Get minimum order quantity
  const minimumOrderQty = product?.minimumOrderQty || product?.min_order_qty || 1;

  // Determine if there's a valid offer/discount
  const hasValidOffer = product.offerPrice && product.price && parseFloat(product.offerPrice) < parseFloat(product.price);

  // Calculate discount percentage
  const calculateDiscount = () => {
    if (!hasValidOffer) return null;
    const original = parseFloat(product.price);
    const sale = parseFloat(product.offerPrice);
    return Math.round(((original - sale) / original) * 100);
  };

  const discountPercentage = calculateDiscount();

  // Determine display prices
  const displayPrice = hasValidOffer ? product.offerPrice : product.price;
  const originalDisplayPrice = hasValidOffer ? product.price : null;

  // Alternative images for hover effect
  const hoverImages = {

  };

  const currentImage = isHovered && hoverImages[product.image] ? hoverImages[product.image] : product.image;

  const handleCardClick = (e) => {
    // Don't navigate if clicking on the cart button
    if (e.target.closest('button[aria-label*="cart"]')) {
      return;
    }

    // Navigate to product page
    navigate(`/product/${product.id}`);
  };

  const handleCartClick = (e) => {
    e.stopPropagation();

    const user = JSON.parse(localStorage.getItem("user"));

    if (!user) {
      navigate("/signin");
      return;
    }

    try {
      // Use category name from product data (from API) or fallback to categoryMap
      const categoryName = product.categoryName || categoryMap[String(product.categoryId)] || product.categoryId || 'Uncategorized';

      // Add product to cart with minimum order quantity - match the working implementation exactly
      addToCart({
        id: product.id,
        name: product.name,
        price: displayPrice,
        image: product.image,
        categoryId: categoryName, // Send category name from API or fallback
        min_order_qty: minimumOrderQty,
        minimumOrderQty: minimumOrderQty
      }, minimumOrderQty); // Use minimum order quantity instead of 1

      // Show success toast with quantity information
      toast.success(`${minimumOrderQty > 1 ? `${minimumOrderQty} × ` : ''}${product.name} added to cart!`, {
        icon: <ShoppingCart size={16} className="text-green-600" />,
        duration: 2000,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add item to cart');
    }
  };

  if (!product.id) return null; // safety guard

  return (
    <article
      onClick={handleCardClick}
      className={`flex flex-col h-full p-4 mx-auto w-full bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer group ${className}`}
      role="article"
      aria-labelledby={`product-${product.id}-name`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Discount Ribbon */}
      {discountPercentage && (
        <div className="absolute top-0 left-4 z-20 transform translate-y-0">
          <div className="relative">
            {/* Main ribbon body */}
            <div className="relative w-12 bg-gradient-to-b from-red-500 via-red-600 to-red-700 shadow-2xl">
              {/* Top rounded cap */}
              <div className="w-full h-0 bg-gradient-to-b from-red-400 to-red-500 rounded-t-lg"></div>

              {/* Content area */}
              <div className="px-1 py-3 text-center relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="w-full h-full bg-gradient-to-br from-white via-transparent to-transparent"></div>
                </div>

                {/* Percentage number */}
                <div className="relative z-10 text-white font-black text-xl leading-none mb-1 drop-shadow-lg">
                  {discountPercentage}%
                </div>

                {/* OFF text */}
                <div className="relative z-10 text-white font-bold text-xs tracking-wider drop-shadow-md">
                  OFF
                </div>

                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 transform -skew-y-12 animate-shine-vertical"></div>
              </div>

              {/* V-shaped bottom */}
              <div className="relative">
                <div className="w-0 h-0 mx-auto border-l-8 border-r-8 border-t-6 border-l-red-700 border-r-red-700 border-t-red-700"></div>
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-6 border-r-6 border-t-4 border-l-red-800 border-r-red-800 border-t-red-800"></div>
              </div>

              {/* Side shadows for 3D effect */}
              <div className="absolute inset-y-0 -left-1 w-1 bg-gradient-to-b from-red-800 to-red-900 opacity-60 rounded-l-sm"></div>
              <div className="absolute inset-y-0 -right-1 w-1 bg-gradient-to-b from-red-300 to-red-400 opacity-40 rounded-r-sm"></div>
            </div>

            {/* Outer glow */}
            <div className="absolute inset-0 bg-red-500 opacity-30 blur-lg scale-100 rounded-lg"></div>

            {/* Floating sparkles */}
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full animate-ping opacity-75"></div>
            <div className="absolute top-6 -left-2 w-1.5 h-1.5 bg-yellow-200 rounded-full animate-pulse opacity-60"></div>
          </div>
        </div>
      )}

      {/* Image Container */}
      <div className="relative overflow-hidden mb-4 rounded-lg">
        <img
          src={currentImage}
          alt={product.name}
          className="object-contain w-full aspect-[0.85] transition-all duration-500 group-hover:scale-105"
          loading="lazy"
        />

        {/* Cart Icon - Top Right */}
        <button
          onClick={handleCartClick}
          className="absolute top-1 right-1 z-10 w-10 h-10 bg-black hover:bg-gray-800 flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-110 group/cart"
          aria-label={`Add ${minimumOrderQty} × ${product.name} to cart`}
          title={`Add ${minimumOrderQty} units to cart${minimumOrderQty > 1 ? ' (minimum order quantity)' : ''}`}
        >
          <ShoppingCart
            size={22}
            className="text-white group-hover/cart:text-gray-200 transition-colors duration-200"
          />
          {/* Show minimum quantity badge if greater than 1 */}
          {/* {minimumOrderQty > 1 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {minimumOrderQty}
            </span>
          )} */}
        </button>

        {/* Overlay effect on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-300 rounded-lg"></div>
      </div>

      {/* Content Container */}
      <div className="flex flex-col flex-1 justify-between">
        {/* Title and Price Section */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3
            id={`product-${product.id}-name`}
            className="flex-1 text-base font-semibold leading-snug text-black capitalize group-hover:text-zinc-700 transition-colors duration-200 truncate "
            title={product.name}
          >
            {product.name}
          </h3>
          <div className="flex items-end gap-2 flex-shrink-0">
            <span
              className={`text-lg font-bold transition-colors duration-200 ${
                hasValidOffer
                  ? 'text-red-500 group-hover:text-red-600'
                  : 'text-black group-hover:text-zinc-700'
              }`}
              aria-label={`${hasValidOffer ? 'Sale' : 'Current'} price ₹${displayPrice}`}
            >
              ₹{displayPrice}
            </span>
            {originalDisplayPrice && (
              <span
                className="text-md text-gray-500 line-through"
                aria-label={`Original price ₹${originalDisplayPrice}`}
              >
                ₹{originalDisplayPrice}
              </span>
            )}
          </div>
        </div>

        {/* Category and Minimum Order Info Section */}
        <div className="flex flex-col gap-1">
          {/* Show category name from API if available */}
          {product.categoryName && (
            <p className="text-xs text-gray-500 truncate">
              {product.categoryName}
            </p>
          )}
          {/* Show minimum order quantity if greater than 1 */}
          {minimumOrderQty > 1 && (
            <p className="text-xs text-blue-600 font-medium">
              Min. order: {minimumOrderQty} units
            </p>
          )}
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes shine-vertical {
          0% { transform: translateY(-100%) skewY(-12deg); }
          100% { transform: translateY(200%) skewY(-12deg); }
        }

        .animate-shine-vertical {
          animation: shine-vertical 3s infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }

        .animate-float {
          animation: float 2s ease-in-out infinite;
        }
      `}</style>
    </article>
  );
}

const Wishlist = () => {
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user")); // get current logged-in user
  const { getUserWishlist, toggleWishlistItem, validateAndCleanWishlist } = useWishlist();

  // State for wishlist items (will be updated after validation)
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Modal state
  const [customizationModal, setCustomizationModal] = useState({
    isOpen: false,
    product: null,
  });

  // Validate wishlist on component mount
  useEffect(() => {
    const validateWishlist = async () => {
      if (!user || hasValidated) return;

      setIsValidating(true);

      try {
        const { validProducts, invalidProducts } = await validateAndCleanWishlist(user.id);

        // Update local state with valid products
        setWishlistItems(validProducts);

        // Show toast message for validation results
        if (invalidProducts.length > 0 && validProducts.length > 0) {
          // Some items were removed, some remain
          toast.success(`Wishlist updated! ${validProducts.length} items available with fresh data, ${invalidProducts.length} unavailable items removed.`, {
            duration: 4000,
            icon: <CheckCircle size={16} className="text-green-600" />,
          });
        } else if (invalidProducts.length > 0 && validProducts.length === 0) {
          // All items were removed
          toast.info("All wishlist items were unavailable and have been removed.", {
            duration: 4000,
            icon: <Trash2 size={16} className="text-red-600" />,
          });
        } else if (validProducts.length > 0 && invalidProducts.length === 0) {
          // All items are valid - show confirmation with data refresh
          toast.success(`All ${validProducts.length} wishlist items updated with latest data!`, {
            duration: 3000,
            icon: <CheckCircle size={16} className="text-green-600" />,
          });
        }

        setHasValidated(true);
      } catch (error) {
        console.error("Error validating wishlist:", error);
        toast.error("Failed to validate wishlist items");

        // Fallback to showing all items from context
        setWishlistItems(getUserWishlist(user.id));
        setHasValidated(true);
      } finally {
        setIsValidating(false);
      }
    };

    // Add a small delay to prevent multiple rapid calls
    const timeoutId = setTimeout(validateWishlist, 100);

    return () => clearTimeout(timeoutId);
  }, [user?.id]); // Only depend on user.id to prevent unnecessary re-runs

  // Update wishlist items when context changes (after manual removal)
  useEffect(() => {
    if (user && hasValidated && !isValidating) {
      const currentWishlist = getUserWishlist(user.id);
      // Only update if the wishlist actually changed
      setWishlistItems(prev => {
        if (JSON.stringify(prev) !== JSON.stringify(currentWishlist)) {
          return currentWishlist;
        }
        return prev;
      });
    }
  }, [user?.id, hasValidated, isValidating]); // Removed getUserWishlist from dependencies

  console.log("Current Wishlist Items:", wishlistItems);

  // Filter wishlist items based on search query
  const filteredWishlistItems = useMemo(() => {
    if (!searchQuery.trim()) return wishlistItems;

    return wishlistItems.filter(
      (item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.categoryName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.categoryId?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [wishlistItems, searchQuery]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleClearAll = () => {
    if (!user) return alert("Please login first!");
    if (
      window.confirm(
        "Are you sure you want to clear all items from your wishlist?",
      )
    ) {
      wishlistItems.forEach((item) => toggleWishlistItem(user.id, item));
      setWishlistItems([]); // Clear local state immediately
      toast.success("Wishlist cleared successfully", {
        icon: <Trash2 size={16} className="text-red-600" />,
      });
    }
  };

  const handleRemoveItem = (item) => {
    if (!user) return alert("Please login first!");
    toggleWishlistItem(user.id, item);
    // Update local state immediately
    setWishlistItems(prev => prev.filter(wishlistItem => wishlistItem.id !== item.id));
    toast.success(`${item.name} removed from wishlist`, {
      icon: <X size={16} className="text-red-600" />,
      duration: 2000,
    });
  };

  const handleCustomizeClick = (product) => {
    setCustomizationModal({
      isOpen: true,
      product,
    });
  };

  const handleCloseModal = () => {
    setCustomizationModal({
      isOpen: false,
      product: null,
    });
  };

  // Split filtered items into rows of 4
  const itemRows = [];
  for (let i = 0; i < filteredWishlistItems.length; i += 4) {
    itemRows.push(filteredWishlistItems.slice(i, i + 4));
  }

  // Show loading state during validation
  if (isValidating) {
    return (
      <div className="relative mx-auto my-0 bg-orange-50 bg-opacity-90 h-auto w-full">
        <Header
          activeSection="wishlist"
          onSearch={handleSearch}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
        />
        <main className="relative mx-auto w-[90%] lg:w-[80%] xl:w-[75%] 2xl:w-[70%] mt-6 md:mt-1 px-4 md:px-0">
          <div className="text-center py-12 md:py-16">
            <div className="max-w-md mx-auto px-4">
              <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">
                Checking your wishlist...
              </h2>
              <p className="text-gray-600 text-sm md:text-base">
                We're verifying that all your saved items are still available.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="relative mx-auto my-0 bg-orange-50 bg-opacity-90 h-auto w-full">
      {/* Header */}
      <Header
        activeSection="wishlist"
        onSearch={handleSearch}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* Main Content */}
      <main className="relative mx-auto w-[90%] lg:w-[80%] xl:w-[75%] 2xl:w-[70%] mt-6 md:mt-1 px-4 md:px-0">
        {/* Wishlist Header */}
        <header className="flex flex-col sm:flex-row gap-4 sm:gap-5 justify-between items-start sm:items-center w-full text-teal-800 mb-6 md:mb-8">
          <div className="flex items-center gap-1">
                      <Heart className="w-8 h-8 text-teal-600" />
                      <h1
            className="text-2xl md:text-3xl lg:text-4xl tracking-wider text-center font-bold text-teal-800 px-3 md:px-2 py-2"
            // style={{ WebkitTextStroke: "1px white", textStroke: "1px white" }}
          >
            Wishlist
          </h1>
                    </div>


          <div className="flex items-center gap-4">
            {/* Search Results Count */}
            {wishlistItems.length > 0 && (
              <span className="text-sm md:text-base text-gray-600">
                {searchQuery
                  ? `${filteredWishlistItems.length} of ${wishlistItems.length}`
                  : wishlistItems.length}{" "}
                item{wishlistItems.length !== 1 ? "s" : ""}
                {searchQuery && ` matching "${searchQuery}"`}
              </span>
            )}

            {wishlistItems.length > 0 && (
              <button
                onClick={handleClearAll}
                className="
                  flex items-center gap-2
                  px-3 md:px-4 py-2 md:py-3
                  text-lg font-bold  md:text-base
                  rounded-xl
                  border border-teal-800
                  text-teal-800
                  bg-[#fff7ed]
                  hover:bg-teal-800
                  hover:text-white
                  transition-all duration-200
                  shadow-md hover:shadow-lg
                  focus:outline-none
                  focus:ring-2 focus:ring-teal-600 focus:ring-offset-2
                "
              >
                <Trash2 className="w-6 h-6" />
                <span className="whitespace-nowrap">Clear All</span>
              </button>
            )}
          </div>
        </header>

        {/* Wishlist Content */}
        {wishlistItems.length === 0 ? (
          <section className="text-center py-8 md:py-10">
            <div className="max-w-md mx-auto px-4">
              <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 md:w-12 md:h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">
                Your wishlist is empty
              </h2>
              <p className="text-gray-600 mb-6 text-sm md:text-base">
                Start adding items you love to your wishlist and they'll appear
                here.
              </p>
              <button
                onClick={() => navigate("/products")}
                className="bg-teal-800 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          </section>
        ) : filteredWishlistItems.length === 0 && searchQuery ? (
          <section className="text-center py-12 md:py-16">
            <div className="max-w-md mx-auto px-4">
              <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 md:w-12 md:h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">
                No items found
              </h2>
              <p className="text-gray-600 mb-6 text-sm md:text-base">
                No wishlist items match your search for "{searchQuery}".
              </p>
              <button
                onClick={() => setSearchQuery("")}
                className="bg-teal-800 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors"
              >
                Show All Items
              </button>
            </div>
          </section>
        ) : (
          <section>
            {itemRows.map((rowItems, rowIndex) => (
              <div key={rowIndex} className="mb-10 md:mb-14">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 justify-items-center">
                  {rowItems.map((product) => (
                    <div
                      key={product.id}
                      className="relative group w-full max-w-[280px] md:max-w-[300px]"
                    >
                      <ProductCard
                        product={product}
                        className="w-full h-auto"
                        onCustomizeClick={handleCustomizeClick}
                      />

                      {/* Remove button */}
                      <button
                        onClick={() => handleRemoveItem(product)}
                        className="absolute top-[0] right-[0] w-7 h-7 md:w-8 md:h-8 bg-red-500 bg-opacity-80 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center hover:bg-red-600"
                        aria-label="Remove from wishlist"
                      >
                        <svg
                          className="w-3 h-3 md:w-4 md:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      {/* Customization Modal */}
      <CustomizationModal
        isOpen={customizationModal.isOpen}
        onClose={handleCloseModal}
        product={customizationModal.product}
      />
    </div>
  );
};

export default Wishlist;
